import { z } from "zod";
import { REGIONS } from "@typedoc/contracts/regions";
import { AUTH_API_BASE } from "./auth-api-base";
import { regionalTokens } from "./access-client";
import { readApiResponse } from "./app-error";
import { boundedRequest } from "./bounded-request";
import { createAuthWorkspacesClient, type AuthWorkspacesClient } from "./rpc/auth-workspaces";

/**
 * The firm named above the workspace list. Region lives here rather than on each workspace: one
 * organisation has one home region and every workspace inherits it.
 */
const organisationSchema = z.object({
  name: z.string().min(1),
  // Null is an organisation that claims no email domain, which is ordinary — a firm created by
  // someone whose address nobody else shares.
  domain: z.string().min(1).nullable(),
  memberCount: z.number().int().nonnegative(),
  region: z.enum(REGIONS),
});

/**
 * One row of the switcher's list. A private workspace the caller may not open is absent from the
 * array rather than flagged, so there is no "may I see this" field to parse.
 */
export const workspaceSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  memberCount: z.number().int().nonnegative(),
  visibility: z.enum(["organisation", "private"]),
  /**
   * Computed by the service from the session's active team. This is the only place the active
   * workspace comes from: the session's organisation field names a different object entirely,
   * and reading it here is what left selection broken.
   */
  active: z.boolean(),
});

export const workspacesResponseSchema = z.object({
  organisation: organisationSchema,
  workspaces: z.array(workspaceSchema),
});
export type WorkspacesResponse = z.infer<typeof workspacesResponseSchema>;
export type WorkspaceOrganisation = z.infer<typeof organisationSchema>;
export type Workspace = z.infer<typeof workspaceSchema>;
export type WorkspaceVisibility = Workspace["visibility"];

/** What the dialog sends. `visibility` is stated rather than defaulted: the form always has one. */
export type CreateWorkspaceRequest = { name: string; visibility: WorkspaceVisibility };

/** The workspace this session is in, or null for a session that has not entered one yet. */
export function activeWorkspace(listing: WorkspacesResponse | undefined): Workspace | null {
  return listing?.workspaces.find((workspace) => workspace.active) ?? null;
}

/** The service answers with the id it activated, which is the acknowledgement this waits for. */
const activatedWorkspaceSchema = z.object({ workspaceId: z.string().min(1) });

// The same base `onboarding.ts` builds: these routes are mounted in the auth service as their own
// Hono sub-app, so they need their own typed client rather than reusing Better Auth's.
export const workspacesClient = createAuthWorkspacesClient(AUTH_API_BASE);

export async function fetchWorkspaces(
  signal: AbortSignal,
  timeoutMs = 10000,
  client: AuthWorkspacesClient = workspacesClient,
): Promise<WorkspacesResponse> {
  return boundedRequest(
    async (requestSignal) =>
      readApiResponse(
        await client.workspaces.$get(undefined, { init: { signal: requestSignal } }),
        workspacesResponseSchema,
      ),
    signal,
    timeoutMs,
  );
}

/**
 * Creating a workspace does not enter it. The service answers the created workspace with
 * `active: false`, and entering it is `switchWorkspace` below — a second, separately refusable
 * write. The answer is one row of the list this module already parses, so it is read with that
 * same schema rather than a looser one made for this call.
 */
export async function createWorkspace(
  input: CreateWorkspaceRequest,
  signal: AbortSignal,
  timeoutMs = 10000,
  client: AuthWorkspacesClient = workspacesClient,
): Promise<Workspace> {
  return boundedRequest(
    async (requestSignal) =>
      readApiResponse(
        await client.workspaces.$post({ json: input }, { init: { signal: requestSignal } }),
        workspaceSchema,
      ),
    signal,
    timeoutMs,
  );
}

/** Only the part of the regional token cache a switch is allowed to touch. */
type ClearableTokens = { clear: () => void };

/**
 * Order matters. The regional token is minted from the server session's active team, so the
 * switch must be acknowledged before anything asks for a token in the new scope — otherwise the
 * mint returns the previous workspace's claims and the cache rejects them as ACCESS_CHANGED.
 *
 * Clearing afterwards also drops the old workspace's token rather than leaving it live for the
 * rest of its five minutes.
 *
 * Better Auth's own `set-active-team` cannot serve this: it refuses every team the caller has no
 * `teamMember` row for, which is every organisation-visible workspace.
 */
export async function switchWorkspace(
  workspaceId: string,
  signal: AbortSignal,
  timeoutMs = 10000,
  client: AuthWorkspacesClient = workspacesClient,
  tokens: ClearableTokens = regionalTokens,
): Promise<void> {
  await boundedRequest(
    async (requestSignal) =>
      readApiResponse(
        await client.workspaces[":workspaceId"].activate.$post(
          // `hc` interpolates a path parameter exactly as given, so an id carrying a slash would
          // reshape the path rather than name a workspace. Hono decodes on the way in, so this
          // encode and that decode are the matching pair.
          { param: { workspaceId: encodeURIComponent(workspaceId) } },
          { init: { signal: requestSignal } },
        ),
        activatedWorkspaceSchema,
      ),
    signal,
    timeoutMs,
  );
  tokens.clear();
}
